import React, { useState, useEffect, useCallback } from 'react';
import GameCard from './GameCard';
import FeaturedGame from './FeaturedGame';
import Footer from './Footer';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';

const ROWS_PER_PAGE = 8;

function getGridColumns() {
  if (typeof window === 'undefined') return 3;
  if (window.matchMedia('(min-width: 1024px)').matches) return 3;
  if (window.matchMedia('(min-width: 640px)').matches) return 2;
  return 1;
}

function GameList() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ownedGameIds, setOwnedGameIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [gridColumns, setGridColumns] = useState(getGridColumns);
  const { isAuthenticated, token } = useAuth();

  useEffect(() => {
    const updateColumns = () => setGridColumns(getGridColumns());
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  const fetchOwnedGames = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/purchases/games`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const ownedIds = data.games.map(g => g.gameId);
        setOwnedGameIds(ownedIds);
      }
    } catch (err) {
      console.error('Error fetching owned games:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchGames();
    if (isAuthenticated && token) {
      fetchOwnedGames();
    }
  }, [isAuthenticated, token, fetchOwnedGames]);

  const fetchGames = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${API_URL}/games`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error('Server error');
      }
      const data = await response.json();
      setGames(data);
      setError(null);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'TypeError' || err.message.includes('fetch') || err.message.includes('Failed to fetch')) {
        setError('server_unavailable');
      } else {
        setError('server_error');
      }
      console.error('Error fetching games:', err);
    } finally {
      setLoading(false);
    }
  };

  const sortedGames = [...games].sort((a, b) => (a.order || 999) - (b.order || 999));
  const featuredGames = sortedGames.filter(game => game.featured === true);
  const regularGames = sortedGames.filter(game => game.featured !== true);
  const gamesPerPage = gridColumns * ROWS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(regularGames.length / gamesPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col w-full">
        <div className="font-sans text-base lg:text-lg text-white text-center py-10">Loading games...</div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col items-center justify-center min-h-[60vh] w-full">
        <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-12 max-w-md text-center mx-2">
          <svg className="w-16 h-16 mx-auto mb-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2 className="font-semibold text-2xl text-white mb-3">
            {error === 'server_unavailable' ? 'Server Unavailable' : 'Server Error'}
          </h2>
          <p className="font-normal text-base text-white/60 mb-6">
            {error === 'server_unavailable' 
              ? "Sorry, we're having trouble connecting to the server. Please make sure the backend is running."
              : "Sorry, something went wrong on our end. Please try again later."}
          </p>
          <button
            onClick={fetchGames}
            className="px-6 py-3 bg-accent-blue/20 border border-accent-blue/30 rounded-xl text-accent-blue font-medium hover:bg-accent-blue/30 hover:border-accent-blue/40 transition-all duration-200"
          >
            Try Again
          </button>
        </div>
      </main>
    );
  }

  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * gamesPerPage;
  const paginatedGames = regularGames.slice(startIndex, startIndex + gamesPerPage);

  const goToPage = (page) => {
    const nextPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const showFeatured = featuredGames.length > 0 && safePage === 1;

  return (
    <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
      {showFeatured && (
        <>
          <div className="featured-align mb-8 lg:mb-12 w-full">
            <div className="mb-4 lg:mb-6">
              <h2 className="font-semibold text-3xl lg:text-4xl text-white">Featured</h2>
            </div>
            {featuredGames.map(game => (
              <FeaturedGame key={game.id} game={game} isOwned={ownedGameIds.includes(game.id)} />
            ))}
          </div>
        </>
      )}
      <div className="w-full">
        <style>{`
          .featured-align {
            margin-top: calc(5.75rem + 0.5rem - 6rem);
          }
          @media (min-width: 768px) {
            .featured-align {
              margin-top: calc(5.75rem + 1.25rem - 8rem);
            }
          }
          .games-align {
            margin-top: calc(5.75rem + 0.5rem - 6rem - 1rem);
          }
          @media (min-width: 768px) {
            .games-align {
              margin-top: calc(5.75rem + 1.25rem - 8rem - 1rem);
            }
          }
        `}</style>
        <div className={`${showFeatured ? 'games-align' : 'featured-align'} mb-4 lg:mb-6 flex items-center justify-between w-full gap-4`}>
          <h2 className="font-semibold text-3xl lg:text-4xl text-white leading-none">Games</h2>
          {totalPages > 1 && (
            <span className="text-xs lg:text-sm font-medium text-white/55 tabular-nums shrink-0 px-3 py-1.5 rounded-full border border-dark-border/80 bg-dark-card/60">
              Page {safePage} of {totalPages}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 w-full max-w-full">
          {paginatedGames.map(game => (
            <GameCard key={game.id} game={game} isOwned={ownedGameIds.includes(game.id)} />
          ))}
        </div>
        {totalPages > 1 && (
          <nav
            className="mt-8 lg:mt-10 flex flex-wrap items-center justify-center gap-2"
            aria-label="Games pagination"
          >
            <button
              type="button"
              onClick={() => goToPage(safePage - 1)}
              disabled={safePage <= 1}
              className="px-4 py-2 rounded-xl text-sm font-medium border border-dark-border bg-dark-card text-white/80 hover:bg-dark-surface hover:text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-dark-card"
            >
              Previous
            </button>
            {pageNumbers.map(page => (
              <button
                key={page}
                type="button"
                onClick={() => goToPage(page)}
                aria-current={page === safePage ? 'page' : undefined}
                className={`min-w-[2.5rem] px-3 py-2 rounded-xl text-sm font-medium border transition-all duration-200 ${
                  page === safePage
                    ? 'bg-accent-blue/20 border-accent-blue/40 text-accent-blue'
                    : 'border-dark-border bg-dark-card text-white/80 hover:bg-dark-surface hover:text-white'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              onClick={() => goToPage(safePage + 1)}
              disabled={safePage >= totalPages}
              className="px-4 py-2 rounded-xl text-sm font-medium border border-dark-border bg-dark-card text-white/80 hover:bg-dark-surface hover:text-white transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-dark-card"
            >
              Next
            </button>
          </nav>
        )}
      </div>
      <Footer />
    </main>
  );
}

export default GameList;
